import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList,
} from "@/components/ui/command";
import { Check, ChevronsUpDown } from "lucide-react";

/**
 * Reusable customer selector with search-as-you-type autocomplete.
 * Same shape and behaviour as the picker used in QuoteForm.jsx —
 * kept as a shared component so Finance forms don't drift.
 */
export default function CustomerCombobox({
  customers,
  value,
  onChange,
  placeholder = "Müşteri seçin…",
  testid = "customer-combobox",
  allowClear = false,
}) {
  const [open, setOpen] = useState(false);
  const selected = customers.find((c) => c.id === value);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full h-10 justify-between rounded-none border-2 border-[#09090B] bg-white font-normal hover:bg-white focus-visible:ring-0"
          data-testid={testid}
        >
          {selected ? selected.name : <span className="text-neutral-400">{placeholder}</span>}
          <ChevronsUpDown size={16} className="opacity-60 shrink-0" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="p-0 rounded-none border-2 border-[#09090B] bg-white"
        style={{ width: "var(--radix-popover-trigger-width)" }}
        align="start"
      >
        <Command
          filter={(v, search) =>
            v.toLocaleLowerCase("tr-TR").includes(search.toLocaleLowerCase("tr-TR")) ? 1 : 0
          }
        >
          <CommandInput
            placeholder="Müşteri ara (isim yazın)…"
            className="h-10 font-mono"
            data-testid={`${testid}-search`}
          />
          <CommandList className="max-h-72">
            <CommandEmpty className="py-6 text-center text-sm text-neutral-500">Eşleşen müşteri yok</CommandEmpty>
            <CommandGroup>
              {allowClear && (
                <CommandItem
                  value=" __clear__"
                  onSelect={() => { onChange(null); setOpen(false); }}
                  className="rounded-none cursor-pointer text-neutral-500 italic"
                  data-testid={`${testid}-clear`}
                >
                  <Check size={14} className={`mr-2 ${!value ? "opacity-100" : "opacity-0"}`} />
                  Müşteri yok
                </CommandItem>
              )}
              {customers.map((c) => (
                <CommandItem
                  key={c.id}
                  value={c.name}
                  onSelect={() => { onChange(c); setOpen(false); }}
                  className="rounded-none cursor-pointer"
                  data-testid={`${testid}-opt-${c.id}`}
                >
                  <Check size={14} className={`mr-2 ${value === c.id ? "opacity-100" : "opacity-0"}`} />
                  <span className="font-medium">{c.name}</span>
                  {c.email && <span className="ml-2 text-xs text-neutral-500 font-mono">{c.email}</span>}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
